<script setup lang="ts">
import { ref, computed } from 'vue';
import ContractFormFields from './ContractFormFields.vue';
import ContractRecipeSection from './ContractRecipeSection.vue';
import { emptyContractForm, validateContractForm, type ContractFormData } from 'src/composables/useContractManagement';
import type { FetchOrganisationResponseLite } from 'src/api/models/fetchOrganisationResponseLite';
import type { FetchUnitResponseLite } from 'src/api/models/fetchUnitResponseLite';
import type { FetchAddressResponse } from 'src/api/models/fetchAddressResponse';
import type { NestedOrganisationContactPerson } from 'src/api/models/nestedOrganisationContactPerson';
import type { FetchRecipeResponseLite } from 'src/api/models/fetchRecipeResponseLite';

defineProps<{
  visible: boolean;
  disabled?: boolean;
  searchOrganisationsFn: (query: string, page: number) => Promise<{ data: FetchOrganisationResponseLite[]; page: number; total_pages: number }>;
  searchUnitsFn: (orgId: string, query: string, page: number) => Promise<{ data: FetchUnitResponseLite[]; page: number; total_pages: number }>;
  searchBranchesFn: (orgId: string | null, query: string, page: number) => Promise<{ data: FetchAddressResponse[]; page: number; total_pages: number }>;
  searchContactPersonsFn: (orgId: string, query: string, page: number) => Promise<{ data: NestedOrganisationContactPerson[]; page: number; total_pages: number }>;
  searchRecipesFn: (query: string, page: number) => Promise<{ data: FetchRecipeResponseLite[]; page: number; total_pages: number }>;
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
  'submit': [data: ContractFormData];
}>();

const formData = ref<ContractFormData>(emptyContractForm());
const submitted = ref(false);

const validationErrors = computed(() => {
  if (!submitted.value) return {};
  return validateContractForm(formData.value);
});

const isValid = computed(() => Object.keys(validationErrors.value).length === 0);

function onSubmit() {
  submitted.value = true;
  if (!isValid.value) return;
  emit('submit', { ...formData.value });
}

function onHide() {
  formData.value = emptyContractForm();
  submitted.value = false;
  emit('update:visible', false);
}
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    header="Nieuw contract"
    :style="{ width: '720px' }"
    :closable="!disabled"
    data-testid="contract-create-dialog"
    @update:visible="onHide"
  >
    <div class="flex flex-col gap-4">
      <ContractFormFields
        v-model="formData"
        :errors="validationErrors"
        :search-organisations-fn="searchOrganisationsFn"
        :search-units-fn="searchUnitsFn"
        :search-branches-fn="searchBranchesFn"
        :search-contact-persons-fn="searchContactPersonsFn"
      />

      <Divider />

      <ContractRecipeSection
        v-model="formData.recipes"
        :submitted="submitted"
        :search-recipes-fn="searchRecipesFn"
      />

      <div v-if="submitted && !isValid" class="text-red-500 text-sm">
        Vul alle verplichte velden in.
      </div>
    </div>

    <template #footer>
      <Button
        data-testid="contract-create-cancel"
        label="Annuleren"
        severity="secondary"
        text
        :disabled="disabled"
        @click="onHide"
      />
      <Button
        data-testid="contract-create-submit"
        label="Contract aanmaken"
        severity="success"
        icon="pi pi-check"
        :loading="disabled"
        @click="onSubmit"
      />
    </template>
  </Dialog>
</template>
